/**
 * @fileoverview HTML Diff Utility
 *
 * Thin wrapper around `htmldiff-js` for use in the precedent AI editor's
 * Review card. Renders a side-by-side or inline diff of the staged
 * proposal against the current template.
 *
 * `htmldiff-js` is HTML-aware — it preserves tag structure and only marks
 * the changed text/inline elements with `<ins>` / `<del>` wrappers. We
 * style those markers under the `.html-diff` scope in `editor-styles.css`.
 */

import * as htmldiffModule from "htmldiff-js";

interface HtmlDiffStatic {
  execute(oldHtml: string, newHtml: string): string;
}

/** Type guard: does `value` expose a callable `execute`? */
function hasExecute(value: unknown): value is HtmlDiffStatic {
  return (
    (typeof value === "function" ||
      (typeof value === "object" && value !== null)) &&
    typeof (value as { execute?: unknown }).execute === "function"
  );
}

/**
 * Locate the `HtmlDiff` class inside whatever `htmldiff-js` resolves to.
 *
 * The package ships a CommonJS webpack bundle whose `module.exports` is
 * `{ __esModule: true, default: HtmlDiff }`. Bundlers disagree on how to
 * expose that to an ES import:
 *
 * - Rollup / Vitest honour `__esModule`, so the namespace *is*
 *   `module.exports` and `.default` is the class.
 * - Rolldown (Vite 8) uses Node-style interop for importers inside a
 *   `"type": "module"` package, so the namespace is
 *   `{ default: module.exports }` and the class sits at `.default.default`.
 *
 * Walking `.default` until we find `execute` works for both, and throws a
 * clear error instead of `undefined is not a function` if a future version
 * changes shape again.
 *
 * @param mod - whatever `import * as m from "htmldiff-js"` evaluated to
 * @returns the `HtmlDiff` class (or any object exposing `execute`)
 * @throws if no `execute` is found within three `.default` hops
 *
 * @example
 * resolveHtmlDiff({ default: { __esModule: true, default: HtmlDiff } }); // HtmlDiff
 * resolveHtmlDiff({ __esModule: true, default: HtmlDiff });             // HtmlDiff
 */
// Three hops covers every known shape: the class itself (0),
// `module.exports` (1), and `{ default: module.exports }` (2).
function unwrapHtmlDiff(candidate: unknown, depth: number): HtmlDiffStatic {
  // Found something that exposes `execute`? That's the class.
  if (hasExecute(candidate)) return candidate;
  if (depth >= 2) {
    throw new Error(
      "htmldiff-js: could not find HtmlDiff.execute on the module export; the package shape may have changed",
    );
  }
  // Otherwise peel one interop layer and try again.
  return unwrapHtmlDiff(
    (candidate as { default?: unknown } | null | undefined)?.default,
    depth + 1,
  );
}

export function resolveHtmlDiff(mod: unknown): HtmlDiffStatic {
  return unwrapHtmlDiff(mod, 0);
}

let cachedHtmlDiff: HtmlDiffStatic | null = null;

/** Resolve the library once, lazily, so a bad shape fails at first use rather than at import. */
function getHtmlDiff(): HtmlDiffStatic {
  cachedHtmlDiff ??= resolveHtmlDiff(htmldiffModule);
  return cachedHtmlDiff;
}

/**
 * Compute an inline HTML diff between two HTML strings.
 *
 * The returned HTML is safe to render under `dangerouslySetInnerHTML` only
 * if both inputs come from a trusted source (the editor's own HTML or the
 * agent's proposed HTML). Both go through the editor's content schema
 * before being persisted, so script tags and the like are stripped.
 *
 * @example
 * const diffMarkup = diffHtml(oldHtml, newHtml);
 * <div className="html-diff" dangerouslySetInnerHTML={{ __html: diffMarkup }} />
 */
export function diffHtml(oldHtml: string, newHtml: string): string {
  if (!oldHtml && !newHtml) return "";
  if (!oldHtml) return newHtml;
  if (!newHtml) return oldHtml;
  return getHtmlDiff().execute(oldHtml, newHtml);
}
